import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { TLeadTask, TMyTasksFilters, TTasksResponse, TUpdateTaskPayload } from "@/models/tasks.model";
import { getMyTasks, getOwnTasks } from "@/services/tasks.service";
import {
  toggleLeadTaskStatus,
  updateLeadTaskFull,
  removeLeadTask,
} from "@/services/leads.service";
import { getErrorFromBackend } from "@/utils/errors";
import { useTasksRefreshStore } from "@/store/tasks/tasksRefresh.store";

const PAGE_SIZE = 25;
// fetchAll consumers (the calendar) need the whole date range at once: use the
// backend's maximum page size so a company-wide month is a handful of requests
// instead of dozens of pages of 25.
const FETCH_ALL_PAGE_SIZE = 500;

export const useMyTasks = (
  filters: TMyTasksFilters = {},
  // "company" hits GET /task (needs tasks.viewAll); "me" hits GET /task/me,
  // which any user can call for their own tasks.
  // fetchAll: walk every page and merge results — for consumers (e.g. the
  // calendar) that need the complete task set rather than one page at a time.
  options: { scope?: "company" | "me"; fetchAll?: boolean } = {}
) => {
  const scope = options.scope ?? "company";
  const fetchAll = options.fetchAll ?? false;
  const { enqueueSnackbar } = useSnackbar();
  const refreshVersion = useTasksRefreshStore((s) => s.version);
  const bumpRefresh = useTasksRefreshStore((s) => s.bump);
  const [tasks, setTasks] = useState<TLeadTask[]>([]);
  const [meta, setMeta] = useState<TTasksResponse["meta"] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TLeadTask | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Stringify so primitive-equal objects don't recreate the callback
  const filtersKey = JSON.stringify(filters);

  // Monotonic id per fetch: a fetch started by newer filters (or a quick view
  // switch) supersedes any in-flight one, whose late responses are dropped
  // instead of overwriting fresher state.
  const fetchSeqRef = useRef(0);

  const fetchTasks = useCallback(async (opts: { silent?: boolean } = {}) => {
    const seq = ++fetchSeqRef.current;
    const isCurrent = () => seq === fetchSeqRef.current;
    try {
      if (!opts.silent) setIsLoading(true);
      const fetch = scope === "me" ? getOwnTasks : getMyTasks;
      const baseFilters = { pageSize: PAGE_SIZE, ...JSON.parse(filtersKey) };

      if (!fetchAll) {
        const response = await fetch(baseFilters);
        if (!isCurrent()) return;
        setTasks(response.data);
        setMeta(response.meta);
        return;
      }

      // Page 1 reveals totalPages; the remaining pages load in parallel so the
      // whole range costs ~2 round trips regardless of size.
      const allFilters = { ...baseFilters, pageSize: FETCH_ALL_PAGE_SIZE };
      const first = await fetch({ ...allFilters, page: 1 });
      if (!isCurrent()) return;
      let allData: TLeadTask[] = first.data;
      const totalPages = first.meta?.totalPages ?? 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetch({ ...allFilters, page: i + 2 })
          )
        );
        if (!isCurrent()) return;
        allData = rest.reduce((acc, r) => acc.concat(r.data), allData);
      }
      setTasks(allData);
      setMeta(first.meta);
    } catch (error) {
      if (!isCurrent()) return;
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    } finally {
      // A superseding fetch owns the spinner now — only the latest one clears it.
      if (!opts.silent && isCurrent()) setIsLoading(false);
    }

  }, [enqueueSnackbar, filtersKey, scope, fetchAll]);

  // Skip the loading spinner when a refetch is only a background sync
  // (another mutation bumped the shared refresh version, filters unchanged) —
  // the mutating handler already applied its own optimistic update, so a
  // full-page spinner here would just flash the list for no reason.
  const prevFiltersKeyRef = useRef(filtersKey);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    const filtersChanged = prevFiltersKeyRef.current !== filtersKey;
    prevFiltersKeyRef.current = filtersKey;
    const silent = !isFirstRunRef.current && !filtersChanged;
    isFirstRunRef.current = false;
    fetchTasks({ silent });

  }, [fetchTasks, refreshVersion, filtersKey]);

  // Resolves true once the new status is saved, false if nothing changed
  // (unknown task or the request failed and was rolled back).
  const handleToggleTask = async (taskId: number): Promise<boolean> => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task?.leadId) return false;

    const newStatus = !task.isComplete;

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, isComplete: newStatus } : t))
    );

    try {
      await toggleLeadTaskStatus({ leadId: task.leadId, taskId, isComplete: newStatus });
      enqueueSnackbar("Task status updated", { variant: "success" });
      bumpRefresh();
      return true;
    } catch (error) {
      // Rollback
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, isComplete: !newStatus } : t))
      );
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
      return false;
    }
  };

  const handleUpdateTask = async (taskId: number, data: TUpdateTaskPayload): Promise<void> => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task?.leadId) return;

    // Backend rejects null values — strip keys whose value is null or undefined
    const payload = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined && v !== null)
    ) as TUpdateTaskPayload;

    try {
      await updateLeadTaskFull({ leadId: task.leadId, taskId, data: payload });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...data } : t))
      );
      enqueueSnackbar("Task updated successfully", { variant: "success" });
      bumpRefresh();
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    }
  };

  const handleDeleteTask = async (taskId: number): Promise<void> => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task?.leadId) return;

    try {
      setIsDeleting(true);
      await removeLeadTask({ leadId: task.leadId, taskId });
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      enqueueSnackbar("Task deleted successfully", { variant: "success" });
      bumpRefresh();
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleComplete = async (taskId: number): Promise<void> => {
    await handleToggleTask(taskId);
  };

  // Keeps a row's commentCount in sync with comments posted or deleted in the task drawer.
  const adjustCommentCount = useCallback((taskId: number, delta: number) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, commentCount: Math.max(0, (t.commentCount ?? 0) + delta) } : t))
    );
  }, []);

  return {
    tasks,
    meta,
    isLoading,
    isDeleting,
    selectedTask,
    setSelectedTask,
    showCreateModal,
    setShowCreateModal,
    handleToggleTask,
    handleUpdateTask,
    handleDeleteTask,
    handleToggleComplete,
    adjustCommentCount,
    fetchTasks,
    bumpTasksRefresh: bumpRefresh,
  };
};
